/**
 * GenericFormPage.js
 *
 * One-question-at-a-time wizard for subcollection-based assessment modules (Adult
 * Autism). Reuses the exact same ADHD visual components (FormCurrentQuestionPanel,
 * FormAnswerSummary, FormCompletionCard, FormProgressBar, FormNavigation) to
 * achieve pixel-perfect parity with the ADHD patient form experience.
 *
 * Data layer:
 * - Answers stored FLAT by serviceUserVarName: { varName: value, ... }
 *   This matches the format expected by buildFormDataShimFromInstance and the
 *   existing production data written by previous GenericFormPage implementations.
 * - Skip tracking via _skip_<questionVarName> sentinel keys
 * - Progress RECOMPUTED from actual answers (never trusts stored progress field)
 * - Blocking save via await updateFormAnswers (Firestore write completes before
 *   advancing to the next question — ADHD parity for data integrity)
 * - useAutismPrefillSync integration preserved
 *
 * The adaptation layer bridges the flat answer format to the ADHD component
 * expectations by:
 * 1. Using React Hook Form (register/watch/setValue) as FormCurrentQuestionPanel expects
 * 2. Injecting answers from the flat map into subquestion.answer for prefill detection
 * 3. Building submittedAnswers for FormAnswerSummary from the flat answers map
 * 4. Converting RHF form data back to flat format on save
 */
import React, { useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ModularPatientSelfDataContext } from '../../../context/ModularPatientSelfDataContext';
import { ModularInformantSelfDataContext } from '../../../context/ModularInformantSelfDataContext';
import { useAuth } from '../../../hooks/useAuth';
import { resolveFormInstanceForDisplay } from '../../../utils/resolveFormTemplates';
import { isModularModule } from '../../../constants/assessmentModules';
import { useAutismPrefillSync } from './hooks/useAutismPrefillSync';
import { formHasPrefillMetadata } from './functions/AutismPrefillFunctions';
import { resolveAnswer, computeScaleResultsForSave } from './functions/scaleScoringAdapter';
import {
  isSubquestionVisible,
  questionHasConditionalSubquestions,
  collectOtherFormKeys,
  buildExternalAnswerLookups,
} from '../../../utils/formVisibility';
import { otherOptionFieldKey } from './utils/sanitizeOptionForKey';
import FormCurrentQuestionPanel from './components/FormCurrentQuestionPanel';
import FormProgressBar from './components/FormProgressBar';
import FormCompletionCard from './components/FormCompletionCard';
import FormLockedCard from './components/FormLockedCard';
import FormNavigation from './components/FormNavigation';
import FormAnswerSummary from './components/FormAnswerSummary';
import ClearedAnswersConfirmDialog from './components/ClearedAnswersConfirmDialog';
import CypSafeguardingNoticeDialog from './components/CypSafeguardingNoticeDialog';
import { getNewlyFlaggedSafeguardingOptions } from './utils/cypSafeguardingAlert';

// ─── Progress Calculation ────────────────────────────────────────────────────
//
// Note: `resolveAnswer` and the scale-scoring adapter (`buildScoringFormData`,
// `computeScaleResultsForSave`) now live in ./functions/scaleScoringAdapter so
// the pure logic can be unit-tested and shared by both save paths. `resolveAnswer`
// is imported at the top of this file and used by the progress/summary helpers
// below.

/**
 * Normalize a stored answer into the format React Hook Form expects for the input.
 * FormCurrentQuestionPanel checks `currentValue === option` (selectOne) or
 * `currentValue.includes(option)` (selectMultiple), so RHF must hold:
 *   - selectOne: a plain string (e.g., "Female")
 *   - selectMultiple: a plain string array (e.g., ["English", "Welsh"])
 *
 * This function handles ALL known storage formats:
 *   - Plain string: "Female" → "Female"
 *   - {value: "Female"} (ADHD format): → "Female"
 *   - {selected: "Female", otherText: "..."} (new modular format): → "Female"
 *   - Plain array: ["English", "Welsh"] → ["English", "Welsh"]
 *   - Array of {value} objects: [{value: "English"}] → ["English"]
 *   - {selectedOptions: ["English"]} (new modular format): → ["English"]
 */
function normalizeForRHF(rawValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;

  // Plain string — use as-is
  if (typeof rawValue === 'string') return rawValue;

  // Plain array (could be string[] or {value}[])
  if (Array.isArray(rawValue)) {
    return rawValue.map(item =>
      typeof item === 'object' && item !== null && item.value !== undefined
        ? item.value
        : item
    );
  }

  // Object with .value (ADHD selectOne format)
  if (typeof rawValue === 'object' && rawValue.value !== undefined) {
    return rawValue.value;
  }

  // Object with .selected (new modular selectOne format)
  if (typeof rawValue === 'object' && rawValue.selected !== undefined) {
    return rawValue.selected;
  }

  // Object with .selectedOptions (new modular selectMultiple format)
  if (typeof rawValue === 'object' && Array.isArray(rawValue.selectedOptions)) {
    return rawValue.selectedOptions;
  }

  return rawValue;
}

/**
 * Extract "other" text values from a stored answer for setting into RHF other fields.
 * Returns a map of { optionValue: otherText }.
 */
function extractOtherTexts(rawValue) {
  if (!rawValue || typeof rawValue !== 'object') return {};

  // {value: "Other", otherValue: "..."} (ADHD selectOne)
  if (rawValue.value && rawValue.otherValue) {
    return { [rawValue.value]: rawValue.otherValue };
  }

  // {selected: "Other", otherText: "..."} (modular selectOne)
  if (rawValue.selected && rawValue.otherText) {
    return { [rawValue.selected]: rawValue.otherText };
  }

  // {selectedOptions: [...], otherTexts: {...}} (modular selectMultiple)
  if (rawValue.otherTexts) {
    return rawValue.otherTexts;
  }

  // [{value: "Other", otherValue: "..."}] (ADHD selectMultiple array)
  if (Array.isArray(rawValue)) {
    const others = {};
    rawValue.forEach(item => {
      if (typeof item === 'object' && item.value && item.otherValue) {
        others[item.value] = item.otherValue;
      }
    });
    return others;
  }

  return {};
}

/**
 * Check if a question was explicitly skipped. Supports both:
 *   - Flat skip marker: answers['_skip_<questionVarName>'] = true
 *   - Nested skip marker: answers[questionKey] = { skipped: true }
 */
function isQuestionSkipped(answers, questionKey, questionVarName) {
  if (!answers) return false;
  if (answers[`_skip_${questionVarName}`]) return true;
  if (answers[questionKey] && typeof answers[questionKey] === 'object' && answers[questionKey].skipped) return true;
  return false;
}

/**
 * Build a flat answer lookup keyed by serviceUserVarName, for evaluating
 * conditionsToShow.
 *
 * Unlike ADHD's buildAnswerLookup (which reads subquestion.answer embedded in
 * formSubsections), answers here live in a separate flat map, so the lookup is
 * built by walking the form structure and resolving each value from that map.
 *
 * Keyed by variable name across the WHOLE form, so a condition can gate on an
 * answer given in another question or subsection — which is what lets a
 * consent question disappear when an earlier question established it does not
 * apply.
 */
function buildFlatAnswerLookup(formDefinition, answers) {
  const answerLookup = {};
  Object.entries(formDefinition?.formSubsections || {}).forEach(([, subsection]) => {
    if (!subsection.questions) return;
    Object.entries(subsection.questions).forEach(([qKey, question]) => {
      Object.values(question.subquestions || {}).forEach(sq => {
        const varName = sq.serviceUserVarName;
        if (varName) {
          const rawVal = resolveAnswer(answers, qKey, varName);
          // Normalise modular selectMultiple answers ({selectedOptions: [...]})
          // to plain arrays so isSubquestionVisible can compare individual
          // option strings against conditionsToShow rules.
          const normalized = (rawVal && typeof rawVal === 'object' && Array.isArray(rawVal.selectedOptions))
            ? rawVal.selectedOptions : rawVal;
          answerLookup[varName] = normalized !== undefined ? normalized : null;
        }
      });
    });
  });
  return answerLookup;
}

/**
 * Format a single raw answer value into the human-readable shape used by the
 * answer summary and the cleared-answers confirmation dialog. Factored out of
 * buildSubmittedAnswers so the dialog can show answers in the same format the
 * respondent has already seen.
 *
 * Returns:
 *   - A plain string for simple values (e.g. "Yes", "1990-01-15")
 *   - An array of {value, otherValue?} objects for selectMultiple
 *   - The raw value as-is for unrecognised shapes (defensive)
 *   - "No answer given" for null/undefined/empty
 */
function formatAnswerForDisplay(rawValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return "No answer given";
  }
  if (typeof rawValue === 'string') {
    return rawValue;
  }
  if (Array.isArray(rawValue)) {
    return rawValue.map(opt =>
      typeof opt === 'string' ? { value: opt } : opt
    );
  }
  if (typeof rawValue === 'object' && rawValue.value !== undefined) {
    return rawValue;
  }
  if (typeof rawValue === 'object' && rawValue.selected) {
    return rawValue.otherText
      ? { value: rawValue.selected, otherValue: rawValue.otherText }
      : rawValue.selected;
  }
  if (typeof rawValue === 'object' && Array.isArray(rawValue.selectedOptions)) {
    return rawValue.selectedOptions.map(opt => ({
      value: opt,
      ...(rawValue.otherTexts?.[opt] ? { otherValue: rawValue.otherTexts[opt] } : {}),
    }));
  }
  return rawValue;
}

/**
 * Identify subquestions across the WHOLE form that are now hidden (per
 * conditionsToShow) but still carry a non-empty stored answer. Returns rich
 * entries suitable for both the confirmation dialog and the null-clearing
 * payload.
 *
 * Each entry contains:
 *   - varName:         the serviceUserVarName whose answer will be cleared
 *   - questionKey:     the form question key (e.g. "2")
 *   - subsectionKey:   the form subsection key (e.g. "3")
 *   - questionTitle:   human-readable title for the confirmation dialog
 *   - answer:          the current answer, formatted via formatAnswerForDisplay
 *
 * The function loops to a fixpoint (bounded at 5 iterations) because clearing
 * one answer can make a second gate evaluate to "hidden" (cascades). On each
 * pass, newly-hidden entries are collected and their values set to null in a
 * working copy of the answers. Convergence is guaranteed because each pass can
 * only shrink the set of visible subquestions.
 *
 * Cross-form (`otherForm`) conditions are evaluated with `externalLookups`,
 * but note what this means in practice: a change on a SIBLING form never runs
 * this function for the current form, so answers hidden purely by a sibling's
 * answer are left stored (and reappear if the sibling answer flips back). Only
 * a save on THIS form can clear anything, and then only what is hidden at that
 * moment.
 *
 * @param {object} formDefinition - The full formDefinition with formSubsections
 * @param {object} mergedAnswers  - Flat answers map (current + pending updates merged)
 * @param {object} [externalLookups={}] - Map of formKey -> sibling flat lookup
 * @returns {Array}  Rich entries for hidden-but-answered subquestions
 */
function collectNewlyHiddenAnswers(formDefinition, mergedAnswers, externalLookups = {}) {
  if (!formDefinition?.formSubsections) return [];

  const allEntries = [];
  // Work on a mutable copy so cascade passes see the effects of earlier clears.
  const workingAnswers = { ...mergedAnswers };
  const MAX_PASSES = 5;

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const answerLookup = buildFlatAnswerLookup(formDefinition, workingAnswers);
    const passEntries = [];

    Object.entries(formDefinition.formSubsections).forEach(([subsectionKey, subsection]) => {
      if (!subsection.questions) return;
      Object.entries(subsection.questions).forEach(([questionKey, question]) => {
        Object.values(question.subquestions || {}).forEach(sq => {
          const varName = sq.serviceUserVarName;
          if (!varName) return;
          // Only care about subquestions that have conditionsToShow gates.
          if (!sq.conditionsToShow) return;
          // Already collected on a previous pass — skip.
          if (allEntries.some(e => e.varName === varName)) return;

          if (!isSubquestionVisible(sq, answerLookup, externalLookups)) {
            // Check if there is actually a stored answer to clear.
            const rawVal = resolveAnswer(workingAnswers, questionKey, varName);
            if (rawVal === null || rawVal === undefined || rawVal === '') return;
            if (Array.isArray(rawVal) && rawVal.length === 0) return;

            passEntries.push({
              varName,
              questionKey,
              subsectionKey,
              questionTitle: question.questionTitle,
              answer: formatAnswerForDisplay(rawVal),
            });
          }
        });
      });
    });

    if (passEntries.length === 0) break; // Fixpoint reached — no new hides.

    // Apply clears into the working copy for the next pass.
    passEntries.forEach(entry => {
      workingAnswers[entry.varName] = null;
    });
    allEntries.push(...passEntries);
  }

  return allEntries;
}

/**
 * Calculate form completion progress based on how many questions have at least
 * one answer (or are intentionally skipped). Returns a 0-1 float.
 * Always recomputed from actual answers — never trusts the stored progress field.
 *
 * Visibility-aware: builds a flat answer lookup keyed by serviceUserVarName so
 * that conditionsToShow rules on subquestions can be evaluated. Only visible
 * subquestions count toward progress. If all subquestions in a question are hidden
 * (e.g. gating answer hides all follow-ups), the question is excluded from the
 * total entirely — matching ADHD's recomputeProgress semantics.
 *
 * `externalLookups` lets `otherForm` conditions see sibling-form answers, so a
 * question hidden by another form (the CYP periods questions once the parent
 * has said the child is Male) drops out of the denominator too. Otherwise a
 * form could never reach 100% because of a question the parent cannot see.
 */
function calculateProgress(formDefinition, answers, externalLookups = {}) {
  if (!formDefinition?.formSubsections) return 0;

  const answerLookup = buildFlatAnswerLookup(formDefinition, answers);

  let totalQuestions = 0;
  let answeredQuestions = 0;

  Object.entries(formDefinition.formSubsections).forEach(([, subsection]) => {
    if (!subsection.questions) return;
    Object.entries(subsection.questions).forEach(([qKey, question]) => {
      const qVarName = question.questionVarName || qKey;

      // Determine which subquestions are visible given current answers
      const allSubquestions = question.subquestions || {};
      const visibleSubquestions = Object.values(allSubquestions).filter(
        sq => isSubquestionVisible(sq, answerLookup, externalLookups)
      );

      // If zero subquestions are visible, skip this question entirely — it shouldn't
      // count toward the total (mirrors ADHD recomputeProgress behaviour).
      if (visibleSubquestions.length === 0) return;

      totalQuestions++;

      if (isQuestionSkipped(answers, qKey, qVarName)) {
        answeredQuestions++;
        return;
      }

      // For conditional questions (at least one subquestion has conditionsToShow),
      // ALL visible subquestions must be answered. For non-conditional questions,
      // at least one subquestion answered suffices (some fields are genuinely optional).
      const isConditional = questionHasConditionalSubquestions(question);
      const checker = isConditional ? 'every' : 'some';

      const hasAnswer = visibleSubquestions[checker](sq => {
        // Subquestions marked isOptional (e.g. "Tell us more" free-text
        // follow-ups) never block question completion — they count as
        // answered whether the patient typed something or not.
        if (sq.isOptional) return true;
        const val = resolveAnswer(answers, qKey, sq.serviceUserVarName);
        if (val === null || val === undefined || val === '') return false;
        if (val === '__SKIPPED__') return true;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'object') {
          if (val.selected) return true;
          if (val.value !== undefined && val.value !== '') return true;
          if (Array.isArray(val.selectedOptions) && val.selectedOptions.length > 0) return true;
          return false;
        }
        return true;
      });

      if (hasAnswer) answeredQuestions++;
    });
  });

  return totalQuestions > 0 ? answeredQuestions / totalQuestions : 0;
}

/**
 * Map a 0-1 progress value to the stored form status.
 *
 * One place for the rule every save path (and the cross-form reconciliation
 * effect) uses, so they cannot drift: 1 is "completed", anything above 0 is
 * "inProgress", 0 is "notStarted".
 */
function deriveStatusFromProgress(progress) {
  if (progress >= 1) return 'completed';
  return progress > 0 ? 'inProgress' : 'notStarted';
}

/**
 * Check if a question is answered (has at least one non-empty subquestion value or is skipped).
 */
function isQuestionAnswered(questionKey, question, answers) {
  const qVarName = question?.questionVarName || questionKey;
  if (isQuestionSkipped(answers, questionKey, qVarName)) return true;

  const subquestions = question?.subquestions || {};
  return Object.values(subquestions).some(sq => {
    const val = resolveAnswer(answers, questionKey, sq.serviceUserVarName);
    if (val === null || val === undefined || val === '') return false;
    if (val === '__SKIPPED__') return true;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') {
      if (val.selected) return true;
      if (val.value !== undefined && val.value !== '') return true;
      if (Array.isArray(val.selectedOptions) && val.selectedOptions.length > 0) return true;
      return false;
    }
    return true;
  });
}

/**
 * Does this question still need the user's attention?
 *
 * True when the question is completely unanswered (and not skipped), OR when
 * its only answers came from provider prefill at setup time and the user has
 * not yet explicitly confirmed them by pressing Next/Submit.
 *
 * This is the single rule for "where should the wizard put the user next?".
 * It drives the resume-on-mount effect AND the post-save navigation in
 * finalizeSave, so a question can never be stranded: if a gating edit reveals
 * a question earlier in the sequence (e.g. switching "Has Alex started their
 * periods?" from No back to Yes brings back the follow-up), it is picked up
 * immediately rather than only on the next page load.
 *
 * @param {object} item                 - A questionSequence entry
 * @param {object} answers              - Flat answers map
 * @param {string[]} providerPrefilledKeys - serviceUserVarNames the provider prefilled
 * @param {object} confirmedQuestions   - { [questionKey]: true } for user-confirmed questions
 * @returns {boolean}
 */
function questionNeedsAttention(item, answers, providerPrefilledKeys, confirmedQuestions) {
  if (!isQuestionAnswered(item.questionKey, item.question, answers)) return true;

  // Answered — but only by provider prefill the user has not reviewed yet?
  if (providerPrefilledKeys.length > 0) {
    const sqVarNames = Object.values(item.question.subquestions || {}).map(sq => sq.serviceUserVarName);
    const isProviderPrefilled = sqVarNames.some(v => providerPrefilledKeys.includes(v));
    if (isProviderPrefilled && !confirmedQuestions[item.questionKey]) return true;
  }
  return false;
}

/**
 * Build a currentQuestion object with answers injected into each subquestion's
 * .answer field — this is what FormCurrentQuestionPanel reads for prefill detection.
 * Reads from the flat answers map (answers[varName]) with nested fallback.
 */
function buildQuestionWithAnswers(question, questionKey, answers, providerPrefilledKeys, confirmedQuestions) {
  if (!question?.subquestions) return question;

  const enrichedSubquestions = {};
  let hasAnyExistingAnswer = false;

  Object.entries(question.subquestions).forEach(([sqIdx, sq]) => {
    const varName = sq.serviceUserVarName;
    const rawValue = resolveAnswer(answers, questionKey, varName);

    // Convert modular answer format to what FormCurrentQuestionPanel expects
    let answer = rawValue;
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      answer = null;
    } else if (Array.isArray(rawValue)) {
      // Array of {value} objects (ADHD format) or plain string array (old format)
      answer = rawValue.map(item =>
        typeof item === 'object' && item !== null && item.value !== undefined
          ? item
          : typeof item === 'string' ? { value: item } : item
      );
      hasAnyExistingAnswer = rawValue.length > 0;
    } else if (typeof rawValue === 'object' && rawValue.value !== undefined) {
      // ADHD selectOne format: {value: "Female", otherValue?: "..."}
      answer = rawValue;
      hasAnyExistingAnswer = true;
    } else if (typeof rawValue === 'object' && rawValue.selected) {
      // Modular selectOne format: {selected: "Female", otherText?: "..."}
      answer = { value: rawValue.selected, otherValue: rawValue.otherText || '' };
      hasAnyExistingAnswer = true;
    } else if (typeof rawValue === 'object' && Array.isArray(rawValue.selectedOptions)) {
      answer = rawValue.selectedOptions.map(opt => ({
        value: opt,
        ...(rawValue.otherTexts?.[opt] ? { otherValue: rawValue.otherTexts[opt] } : {}),
      }));
      hasAnyExistingAnswer = true;
    } else if (typeof rawValue === 'string' && rawValue !== '__SKIPPED__') {
      hasAnyExistingAnswer = true;
    } else if (rawValue !== '__SKIPPED__') {
      hasAnyExistingAnswer = true;
    }

    enrichedSubquestions[sqIdx] = { ...sq, answer };
  });

  // Determine hasSeenQuestion: if any subquestion was provider-prefilled, require
  // explicit user confirmation before marking as "seen" (enables the prefill banner
  // in FormCurrentQuestionPanel). For user-originated answers, having data = seen.
  const sqVarNames = Object.values(question.subquestions).map(sq => sq.serviceUserVarName);
  const hasProviderPrefill = providerPrefilledKeys.length > 0
    && sqVarNames.some(v => providerPrefilledKeys.includes(v));

  const hasSeenQuestion = hasProviderPrefill
    ? !!confirmedQuestions[questionKey]
    : hasAnyExistingAnswer;

  return { ...question, subquestions: enrichedSubquestions, hasSeenQuestion };
}

/**
 * Build the submittedAnswers array for FormAnswerSummary from the flat answers map.
 * Each entry matches the shape expected by the ADHD component:
 * { questionTitle, questionVarName, answers: [{question, answer, serviceUserVarName}], wasSkipped }
 */
function buildSubmittedAnswers(questionSequence, answers) {
  const submitted = [];

  questionSequence.forEach(item => {
    const question = item.question;
    const questionKey = item.questionKey;
    const qVarName = question?.questionVarName || questionKey;
    const wasSkipped = isQuestionSkipped(answers, questionKey, qVarName);

    // Check if this question has ANY data (answered or skipped)
    const subquestions = question?.subquestions || {};
    const hasAnyData = wasSkipped || Object.values(subquestions).some(sq => {
      const val = resolveAnswer(answers, questionKey, sq.serviceUserVarName);
      return val !== undefined && val !== null && val !== '';
    });

    if (!hasAnyData) return;

    const answerEntries = Object.entries(subquestions).map(([, sq]) => {
      const varName = sq.serviceUserVarName;
      let answer = null;

      if (wasSkipped) {
        answer = question.skipButtonText || "I'm unable to provide this information";
      } else {
        const rawValue = resolveAnswer(answers, questionKey, varName);
        answer = formatAnswerForDisplay(rawValue);
      }

      return {
        question: sq.subquestionDisplayText,
        answer,
        serviceUserVarName: varName,
      };
    });

    submitted.push({
      questionTitle: question.questionTitle,
      questionVarName: qVarName,
      answers: answerEntries,
      wasSkipped,
      ...(wasSkipped && question.skipButtonText ? { skipButtonText: question.skipButtonText } : {}),
    });
  });

  return submitted;
}

/**
 * Build the flat, ordered list of visible questions for the wizard to walk.
 *
 * A question is "visible" when at least one of its subquestions passes its
 * conditionsToShow check (or has no conditions). The list is recomputed from
 * the current answers, so a gating change (e.g. selecting "Birth Parent" hides
 * the "How long have you known them?" question) immediately shrinks or grows
 * the sequence.
 *
 * This is a pure function (no side effects, no React state) so it can be called
 * both from the useMemo that drives rendering AND inline inside handleNextQuestion
 * after a save. The latter is critical: after an edit-save the closure still holds
 * the pre-save questionSequence, but the answers have changed. Rebuilding the
 * sequence from mergedAllAnswers lets the navigation code pick the correct next
 * question in the up-to-date list rather than indexing into a stale one.
 *
 * `externalLookups` carries sibling-form answers for `otherForm` conditions,
 * so a question whose every subquestion is hidden by another form (the CYP
 * periods questions for a child recorded as Male) is left out of the walk.
 */
function buildQuestionSequence(formDefinition, answers, externalLookups = {}) {
  if (!formDefinition?.formSubsections) return [];

  const answerLookup = buildFlatAnswerLookup(formDefinition, answers);
  const sequence = [];
  const subsectionKeys = Object.keys(formDefinition.formSubsections)
    .sort((a, b) => Number(a) - Number(b));

  subsectionKeys.forEach(subsectionKey => {
    const subsection = formDefinition.formSubsections[subsectionKey];
    if (!subsection?.questions) return;

    const questionKeys = Object.keys(subsection.questions)
      .sort((a, b) => Number(a) - Number(b));

    questionKeys.forEach(questionKey => {
      const question = subsection.questions[questionKey];
      const subquestions = Object.values(question.subquestions || {});
      const hasVisibleSubquestion = subquestions.length === 0
        || subquestions.some(sq => isSubquestionVisible(sq, answerLookup, externalLookups));
      if (!hasVisibleSubquestion) return;

      sequence.push({
        subsectionKey,
        questionKey,
        question,
        subsectionTitle: subsection.formSubsectionTitle || null,
      });
    });
  });

  return sequence;
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export default function GenericFormPage() {
  const { formKey } = useParams();
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const patientCtx = useContext(ModularPatientSelfDataContext);
  const informantCtx = useContext(ModularInformantSelfDataContext);

  // Any informant on a subcollection-based pathway (Adult Autism, CYP ADHD, and
  // future modular pathways) reads their forms from the informant context and
  // answers ABOUT someone else, so their form text needs the patient's name
  // substituted in. This deliberately keys off the module registry rather than
  // testing for 'adultAutism': hardcoding the autism module here would send a
  // CYP ADHD informant to the patient context, where their forms do not exist,
  // and leave {{patientFirstName}} tokens unresolved on screen.
  const isModularInformantForm =
    claims?.userType === 'Informant' && isModularModule(claims?.assessmentModule);

  const dashboardPath = isModularInformantForm
    ? '/informant/modular-dashboard'
    : '/service-user/modular-dashboard';

  const {
    formInstances,
    updateFormAnswers,
  } = isModularInformantForm ? informantCtx : patientCtx;

  const patientFirstName = isModularInformantForm ? (informantCtx?.patientFirstName || '') : '';
  const linkedPatientId = isModularInformantForm ? (informantCtx?.linkedPatientId || null) : null;

  // CYP ADHD form locking: the server-side sync denormalises the effective lock
  // onto the informant profile. We read it via the context and fold it into the
  // page-level isFormLocked flag further down. Non-CYP informants and direct
  // patients always get false here.
  const cypFormsLocked = isModularInformantForm ? (informantCtx?.formsLocked ?? false) : false;

  const rawFormInstance = formInstances?.[formKey];

  const formInstance = useMemo(() => {
    if (!rawFormInstance) return null;
    if (!isModularInformantForm) return rawFormInstance;
    return resolveFormInstanceForDisplay(rawFormInstance, patientFirstName);
  }, [rawFormInstance, isModularInformantForm, patientFirstName]);

  const formDefinition = formInstance?.formDefinition;
  const answers = rawFormInstance?.answers || {};

  // Provider prefill tracking: which answer keys were written by the provider at
  // patient setup time (set once in setupNewPatient, never modified after).
  const providerPrefilledKeys = rawFormInstance?.providerPrefilledKeys || [];

  // User confirmation tracking: which questions the user has explicitly submitted
  // via the Next/Submit button (built up incrementally as the user progresses).
  const confirmedQuestions = rawFormInstance?.confirmedQuestions || {};

  // Prefill sync: writes formatted answers to assessmentStages/history.
  // CYP ADHD is excluded — its prefill runs server-side (autism_progress_trigger)
  // into the cyp_adhd subcollection; the client sync would write to an
  // adult_autism/.../history path that does not exist for CYP informants.
  const hasPrefill = useMemo(() => formHasPrefillMetadata(rawFormInstance), [rawFormInstance]);
  const currentUserUid = user?.uid || null;
  const isCypAdhdForm = claims?.assessmentModule === 'cypAdhd';

  useAutismPrefillSync({
    enabled: hasPrefill && !!currentUserUid && !isCypAdhdForm,
    formKey,
    formInstance: rawFormInstance,
    actorType: isModularInformantForm ? 'informant' : 'patient',
    actorUid: currentUserUid,
    patientId: isModularInformantForm ? linkedPatientId : currentUserUid,
  });

  // ─── React Hook Form (required by FormCurrentQuestionPanel) ─────────────────

  const { register, handleSubmit, reset, setValue, watch, control, getValues } = useForm();

  // ─── Question Sequence ──────────────────────────────────────────────────────

  // The wizard walks this sequence one question at a time.
  //
  // Questions whose subquestions are ALL hidden by conditionsToShow are left
  // out. Without this the wizard navigates to a question it then renders empty:
  // answer "No" to "Does Alex have a preferred name?" and you were still taken
  // to "If yes, what name do they like to be called?". calculateProgress has
  // always excluded those questions, so the sequence and the progress bar
  // disagreed about which questions exist.
  //
  // Safe for the adult pathways: a question only vanishes when EVERY one of its
  // subquestions is conditional, and none of the 783 adult ADHD or autism
  // questions is (adult forms gate individual follow-ups, never a whole
  // question). test_form_engine_question_visibility.py asserts that for all six
  // form models the engine renders, so it stays true.
  // ─── Cross-form gating (otherForm conditions) ──────────────────────────────
  //
  // Some CYP ADHD questions depend on an answer given on a DIFFERENT form: the
  // puberty/periods questions on "Their early life" are hidden once the parent
  // has said on the About form that the child's sex assigned at birth is Male
  // or Intersex. The informant context already holds every form instance, so
  // the sibling answers are available live, with no extra reads.
  //
  // `otherFormKeys` is the list of sibling forms this form's definition refers
  // to. It is empty for every adult form and every CYP form other than Their
  // early life, and everything below keys off it, so those forms are
  // untouched by this feature (same sequence, same progress, no extra writes).
  const otherFormKeys = useMemo(() => collectOtherFormKeys(formDefinition), [formDefinition]);
  const hasOtherFormDependency = otherFormKeys.length > 0;

  // Flat lookups for just the referenced sibling forms. Recomputed only when
  // one of those sibling instances changes, not on every answer typed here.
  const externalLookups = useMemo(
    () => buildExternalAnswerLookups(
      formDefinition,
      formInstances,
      (sibling) => buildFlatAnswerLookup(sibling.formDefinition, sibling.answers || {}),
    ),
    // formInstances is the dependency because the referenced siblings live
    // inside it; buildExternalAnswerLookups only touches the keys it needs.
    [formDefinition, formInstances],
  );

  const questionSequence = useMemo(
    () => buildQuestionSequence(formDefinition, answers, externalLookups),
    [formDefinition, answers, externalLookups],
  );

  // Cross-question answer lookup for subquestion-level conditionsToShow.
  //
  // FormCurrentQuestionPanel evaluates conditionsToShow with a live answer
  // lookup that only contains the CURRENT question's subquestions. When a
  // condition gates on a variable from a DIFFERENT question (e.g. Q2's
  // howLongKnown gating on Q1's relationshipToChild in About you), the
  // gating value is missing and the subquestion renders greyed out.
  //
  // This lookup covers ALL questions so deriveQuestionValidation can seed
  // its live lookup with cross-question values. Same-question variables
  // are overwritten by the live RHF values inside deriveQuestionValidation,
  // so same-question conditions remain fully reactive.
  const storedAnswerLookup = useMemo(
    () => buildFlatAnswerLookup(formDefinition, answers),
    [formDefinition, answers],
  );

  // ─── Form State (mirrors ADHD PatientFormTemplate's formState shape) ────────

  const [formState, setFormState] = useState({
    currentPanel: 1,
    currentQuestion: 1,
    isEditing: false,
    editIndex: null,
    showOtherInput: false,
    isSkipping: false,
    submittedAnswers: [],
  });

  // Track current index in the flat question sequence (0-based)
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(null);

  // Tracks whether a Firestore write is in flight (provides save feedback to the user)
  const [isSaving, setIsSaving] = useState(false);

  // Track whether confetti has shown this session
  const hasShownConfetti = useRef(false);

  // Gate confetti behind user action: only show celebration after the user has
  // actively submitted on the final unanswered question at least once. Without this,
  // provider prefill pushing progress to 100% would surprise the user with confetti
  // before they've reviewed/confirmed anything — matching ADHD's behaviour.
  const [hasSubmittedFinalQuestionOnce, setHasSubmittedFinalQuestionOnce] = useState(false);

  // Accessibility: ref for question heading focus management
  const questionHeadingRef = useRef(null);

  // Guard to prevent the navigation prefill effect from re-running on the same question
  // (mirrors ADHD's prevPrefillKeyRef pattern)
  const prevPrefillKeyRef = useRef(null);

  // Remembers which question the user was working on before they clicked "edit"
  // on an earlier answered question. After the edit is saved (or cancelled), the
  // wizard returns here rather than scanning the (possibly stale) question list
  // by numeric index. Mirrors ADHD PatientFormTemplate's formState.lastUnanswered
  // but uses a ref — simpler, no render cost, and GenericFormPage already tracks
  // the cursor via the separate currentQuestionIdx state.
  const lastUnansweredQuestion = useRef(null);

  // --- Cross-question clearing confirmation dialog state ---
  // When a save would silently destroy answers from OTHER questions (not visible
  // on the current screen), the save is paused and the dialog opens. The pending
  // payload sits in the ref while the dialog is shown. On confirm, the clears
  // are folded in and the save completes. On cancel, the ref is cleared and the
  // user stays on the question with nothing saved.
  const [clearedAnswersDialogOpen, setClearedAnswersDialogOpen] = useState(false);
  const pendingSaveRef = useRef(null);
  // Same pattern for Save & Exit: when cross-question clears would occur, the
  // dialog opens and we stash whether this was triggered by the exit path so
  // the confirm handler knows to navigate after saving.
  const [pendingExitSave, setPendingExitSave] = useState(false);

  // ── CYP safeguarding notice dialog state ──────────────────────────────
  // When the parent/carer selects a flagged safety option on the
  // riskBehaviours question, a dismissable notice is shown after the save
  // completes. Unlike the SLaM crisis dialog (AlertDialog, blocking), this
  // uses Radix Dialog and can be closed by Escape, clicking outside, or the
  // Continue button. The email alert fires server-side independently — this
  // dialog is purely presentational reassurance.
  const [safeguardingNoticeOpen, setSafeguardingNoticeOpen] = useState(false);

  // Resume at the first question needing attention on mount. A question "needs
  // attention" if it's either completely unanswered OR has unconfirmed provider
  // prefill (answers exist but the user hasn't explicitly submitted/reviewed).
  useEffect(() => {
    if (questionSequence.length === 0 || currentQuestionIdx !== null) return;

    const firstNeedingAttention = questionSequence.findIndex(item =>
      questionNeedsAttention(item, answers, providerPrefilledKeys, confirmedQuestions)
    );

    const startIdx = firstNeedingAttention >= 0 ? firstNeedingAttention : Math.max(0, questionSequence.length - 1);
    setCurrentQuestionIdx(startIdx);

    // Set initial formState panel/question numbers for the starting position
    const startItem = questionSequence[startIdx];
    if (startItem) {
      // Record the starting question so edit → save / cancel can return here.
      lastUnansweredQuestion.current = {
        subsectionKey: startItem.subsectionKey,
        questionKey: startItem.questionKey,
      };
      setFormState(prev => ({
        ...prev,
        currentPanel: Number(startItem.subsectionKey),
        currentQuestion: Number(startItem.questionKey),
        submittedAnswers: buildSubmittedAnswers(questionSequence, answers),
      }));
    }
  }, [questionSequence, answers, currentQuestionIdx, providerPrefilledKeys, confirmedQuestions]);

  // Keep the cursor inside the sequence. Going back and changing a gating
  // answer can hide questions that came after it, shrinking the sequence under
  // a cursor that was pointing past the new end. Every consumer guards against
  // an undefined item, so nothing crashes — but the cursor would sit on
  // nothing, showing an apparently blank form. Clamping puts it on the last
  // real question instead.
  useEffect(() => {
    if (currentQuestionIdx === null || questionSequence.length === 0) return;
    if (currentQuestionIdx > questionSequence.length - 1) {
      setCurrentQuestionIdx(questionSequence.length - 1);
    }
  }, [currentQuestionIdx, questionSequence.length]);

  // Prefill React Hook Form when navigating to a new question (non-edit transitions).
  // During edit mode, prefill is handled inline in handleEdit to guarantee it always runs.
  useEffect(() => {
    if (currentQuestionIdx === null || questionSequence.length === 0) return;
    // Skip prefill during edit mode — handleEdit does its own inline prefill
    if (formState.isEditing) return;

    const item = questionSequence[currentQuestionIdx];
    if (!item) return;

    // Dedupe: don't re-run if same question index + same answer state
    const prefillKey = `${currentQuestionIdx}_${Object.keys(answers || {}).length}`;
    if (prevPrefillKeyRef.current === prefillKey) return;
    prevPrefillKeyRef.current = prefillKey;

    reset();

    const question = item.question;
    const qVarName = question?.questionVarName || item.questionKey;
    const wasSkipped = isQuestionSkipped(answers, item.questionKey, qVarName);

    if (question?.subquestions && !wasSkipped) {
      Object.entries(question.subquestions).forEach(([sqIdx, sq]) => {
        const varName = sq.serviceUserVarName;
        const rawValue = resolveAnswer(answers, item.questionKey, varName);
        const answerKey = `question_${item.subsectionKey}_${item.questionKey}_${sqIdx}`;
        const normalized = normalizeForRHF(rawValue);

        if (normalized === undefined) return;

        setValue(answerKey, normalized);

        // Set "other" text fields
        const otherTexts = extractOtherTexts(rawValue);
        Object.entries(otherTexts).forEach(([optionVal, otherText]) => {
          if (sq.otherOptions && sq.otherOptions[optionVal]) {
            const otherKey = otherOptionFieldKey(answerKey, optionVal);
            setValue(otherKey, otherText);
            setFormState(prev => ({ ...prev, showOtherInput: true }));
          }
        });
      });
    }
  }, [currentQuestionIdx, questionSequence, answers, reset, setValue, formState.isEditing]);

  // Scroll the page to the progress bar / form top. Called after every save
  // to match ADHD's PatientFormTemplate.scrollToFormTop() behaviour. This
  // ensures the user always sees progress bar increment and — on final
  // question — also sees confetti and the completion card.
  const scrollToFormTop = useCallback(() => {
    setTimeout(() => {
      // Prefer the subsection title so the user lands on the full question
      // context (title + question number); fall back to the previous targets.
      const sectionHeading = document.getElementById('form-section-heading');
      if (sectionHeading) {
        sectionHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const nav = document.getElementById('form-navigation-container');
      if (nav) {
        nav.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const bar = document.getElementById('form-progress-bar-container');
        if (bar) {
          bar.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
  }, []);

  // A form is locked (read-only) if EITHER:
  //   1. The form instance itself carries status 'locked' (legacy Adult ADHD path), OR
  //   2. The CYP informant's assignment is locked (denormalised by server-side sync).
  // Both routes prevent saves and swap the question panel for FormLockedCard.
  const isFormLocked = rawFormInstance?.status === 'locked' || cypFormsLocked;

  // ─── finalizeSave — shared save + navigate tail ─────────────────────────────
  // Extracted so both the direct save path (no dialog needed) and the dialog
  // confirm path share exactly one code path for progress recalculation,
  // Firestore write, sequence rebuild, and navigation.
  const finalizeSave = useCallback(async (answerUpdate, item, mergedAllAnswers) => {
    // Client-side lock guard: if the form was locked between the user starting
    // to answer and clicking Save, bail before hitting Firestore. This is a UX
    // courtesy — the Firestore rules enforce the same restriction server-side.
    if (isFormLocked) {
      toast.error('This questionnaire has been locked. Your answer was not saved.');
      return;
    }

    const newProgress = calculateProgress(formDefinition, mergedAllAnswers, externalLookups);
    const status = deriveStatusFromProgress(newProgress);

    // Mark this question as explicitly confirmed by the user.
    const updatedConfirmed = { ...confirmedQuestions, [item.questionKey]: true };

    const extraFields = { progress: newProgress, status, confirmedQuestions: updatedConfirmed };
    const scaleResults = await computeScaleResultsForSave({
      formKey,
      formDefinition,
      answers: mergedAllAnswers,
      progress: newProgress,
    });
    if (scaleResults) {
      extraFields.scaleResults = scaleResults;
    }

    // Save to Firestore via the blocking write path.
    setIsSaving(true);
    try {
      await updateFormAnswers(formKey, answerUpdate, extraFields);
    } catch (err) {
      console.error('[GenericFormPage] Save failed:', err);
      // Firestore rules reject writes to locked CYP formInstances with
      // PERMISSION_DENIED. Surface a specific message so the informant
      // understands their forms were locked (e.g. while they were mid-answer).
      if (err?.code === 'permission-denied') {
        toast.error('This questionnaire has been locked by the clinical team. Your latest answer was not saved.');
      } else {
        toast.error("We couldn't save your answer. Please check your internet connection and try again.");
      }
      setIsSaving(false);
      return;
    }
    setIsSaving(false);

    if (newProgress >= 1) {
      setHasSubmittedFinalQuestionOnce(true);
    }

    // ── CYP safeguarding notice ───────────────────────────────────────
    // After a successful save, check whether the respondent has NEWLY
    // selected a flagged safety option. The check compares pre-save
    // answers (rawFormInstance.answers snapshot) against the merged
    // post-save answers. Only newly selected options trigger the notice;
    // re-saves, removals, and unrelated questions are silent.
    const preSaveAnswers = rawFormInstance?.answers || {};
    const newlyFlagged = getNewlyFlaggedSafeguardingOptions(
      item.question,
      preSaveAnswers,
      mergedAllAnswers,
    );
    if (newlyFlagged.length > 0) {
      setSafeguardingNoticeOpen(true);
    }

    // Rebuild the visible question sequence from the answers we just saved.
    // The closure's questionSequence was built from the PREVIOUS answers, so
    // we must build a fresh sequence from mergedAllAnswers and navigate by
    // question identity (subsectionKey + questionKey) to guarantee correctness.
    const freshSequence = buildQuestionSequence(formDefinition, mergedAllAnswers, externalLookups);
    const newSubmittedAnswers = buildSubmittedAnswers(freshSequence, mergedAllAnswers);

    // The first question in the UP-TO-DATE sequence that still needs the user.
    // Evaluated against the answers we just saved and the confirmation map we
    // just extended, so it is exactly what the resume-on-mount effect would
    // pick if the page were reloaded right now.
    const firstNeedingAttentionIdx = freshSequence.findIndex(q =>
      questionNeedsAttention(q, mergedAllAnswers, providerPrefilledKeys, updatedConfirmed)
    );

    if (formState.isEditing) {
      // After an edit-save, always resume at the first question needing
      // attention — NOT at the question the user was on before the edit.
      //
      // Why: the wizard only ever moves forward via Save/Skip, so in normal
      // flow the first unanswered question IS the one they were on. The two
      // differ only when the edit changed a gating answer and re-revealed a
      // question that sits earlier in the sequence (e.g. "Has Alex started
      // their periods?" edited No -> Yes brings back "Do difficulties get
      // worse around their period?"). Returning to the remembered question
      // in that case strands the revealed one: progress stalls short of 100%,
      // the last question appears to repeat, and the form cannot complete
      // until the page is reloaded. Landing on the first question needing
      // attention picks the revealed question up straight away.
      //
      // Fallback when nothing needs attention (every question answered and
      // confirmed): return to where they were, else the last question — the
      // completion card takes over from there.
      let nextIdx = firstNeedingAttentionIdx;
      if (nextIdx < 0) {
        const returnTo = lastUnansweredQuestion.current;
        nextIdx = returnTo
          ? freshSequence.findIndex(
              q => q.subsectionKey === returnTo.subsectionKey && q.questionKey === returnTo.questionKey
            )
          : -1;
      }
      if (nextIdx < 0) nextIdx = Math.max(0, freshSequence.length - 1);

      const nextItem = freshSequence[nextIdx];
      // Keep the remembered position in step with where we actually land, so
      // a cancelled follow-up edit returns here rather than to a stale spot.
      lastUnansweredQuestion.current = {
        subsectionKey: nextItem.subsectionKey,
        questionKey: nextItem.questionKey,
      };
      setCurrentQuestionIdx(nextIdx);
      setFormState(prev => ({
        ...prev,
        isEditing: false,
        editIndex: null,
        currentPanel: Number(nextItem.subsectionKey),
        currentQuestion: Number(nextItem.questionKey),
        submittedAnswers: newSubmittedAnswers,
        showOtherInput: false,
      }));
    } else {
      const justSavedIdx = freshSequence.findIndex(
        q => q.subsectionKey === item.subsectionKey && q.questionKey === item.questionKey
      );
      // Normal flow: step to the question after the one just saved.
      const sequentialNextIdx = justSavedIdx >= 0 ? justSavedIdx + 1 : currentQuestionIdx + 1;
      let nextIdx;
      if (sequentialNextIdx <= freshSequence.length - 1) {
        nextIdx = sequentialNextIdx;
      } else if (firstNeedingAttentionIdx >= 0) {
        // We just saved the LAST question in the sequence but something
        // earlier still needs the user (a question re-revealed by a gating
        // edit that was never answered). Go back to it rather than sitting on
        // the final question — which would show it again, already answered,
        // with no way to reach 100%.
        nextIdx = firstNeedingAttentionIdx;
      } else {
        // Everything answered: stay on the last question; the completion
        // card renders in place of the question panel.
        nextIdx = Math.max(0, freshSequence.length - 1);
      }
      const nextItem = freshSequence[nextIdx];

      lastUnansweredQuestion.current = {
        subsectionKey: nextItem.subsectionKey,
        questionKey: nextItem.questionKey,
      };

      setCurrentQuestionIdx(nextIdx);
      setFormState(prev => ({
        ...prev,
        currentPanel: Number(nextItem.subsectionKey),
        currentQuestion: Number(nextItem.questionKey),
        submittedAnswers: newSubmittedAnswers,
        showOtherInput: false,
        isSkipping: false,
      }));
    }

    scrollToFormTop();
  }, [formDefinition, formKey, updateFormAnswers, formState.isEditing, confirmedQuestions, providerPrefilledKeys, currentQuestionIdx, scrollToFormTop, isFormLocked, externalLookups]);

  // ─── handleNextQuestion (called by FormCurrentQuestionPanel on submit) ──────

  const handleNextQuestion = useCallback(async (data, isSkipping = false) => {
    if (!questionSequence[currentQuestionIdx]) return;

    const item = questionSequence[currentQuestionIdx];
    const question = item.question;
    const questionKey = item.questionKey;
    const qVarName = question.questionVarName || questionKey;

    // Convert RHF form data to FLAT answer format (keyed by serviceUserVarName)
    let answerUpdate;
    if (isSkipping) {
      // Store skip marker as a flat key
      answerUpdate = { [`_skip_${qVarName}`]: true };
    } else {
      answerUpdate = {};
      if (question?.subquestions) {
        Object.entries(question.subquestions).forEach(([sqIdx, sq]) => {
          const answerKey = `question_${item.subsectionKey}_${item.questionKey}_${sqIdx}`;
          const value = data[answerKey];
          const varName = sq.serviceUserVarName;

          if (value === undefined || value === null || value === '') {
            return;
          }

          if (sq.inputType === 'selectOne' && sq.otherOptions) {
            const otherKey = otherOptionFieldKey(answerKey, String(value));
            const otherText = data[otherKey];
            if (otherText && sq.otherOptions[value]) {
              answerUpdate[varName] = { selected: value, otherText };
            } else {
              answerUpdate[varName] = value;
            }
          } else if (sq.inputType === 'selectMultiple') {
            const selectedOptions = Array.isArray(value) ? value : [];
            const otherTexts = {};
            selectedOptions.forEach(opt => {
              const otherKey = otherOptionFieldKey(answerKey, opt);
              if (data[otherKey] && sq.otherOptions && sq.otherOptions[opt]) {
                otherTexts[opt] = data[otherKey];
              }
            });
            answerUpdate[varName] = {
              selectedOptions,
              ...(Object.keys(otherTexts).length > 0 ? { otherTexts } : {}),
            };
          } else {
            answerUpdate[varName] = value;
          }
        });
      }
    }

    // --- Unified visibility-aware clearing ---
    // Evaluate conditionsToShow across the WHOLE form using the merged answers
    // (current stored + just-submitted). This replaces the old same-question-only
    // clearing block and also handles cross-question cascades (e.g. editing Q1
    // hides Q2 in a different question). Same-question clears (where the user
    // saw the follow-up grey out on the same screen) are applied silently;
    // cross-question clears trigger the confirmation dialog since those answers
    // are invisible from the current screen.
    const currentAnswers = rawFormInstance?.answers || {};
    const mergedAllAnswers = { ...currentAnswers, ...answerUpdate };
    const hiddenEntries = collectNewlyHiddenAnswers(formDefinition, mergedAllAnswers, externalLookups);

    // Partition: entries belonging to the question just saved clear silently
    // (the amber in-form banner already warned); entries from OTHER questions
    // require user confirmation via the dialog.
    const sameQuestionClears = hiddenEntries.filter(
      e => e.questionKey === questionKey && e.subsectionKey === item.subsectionKey
    );
    const crossQuestionClears = hiddenEntries.filter(
      e => !(e.questionKey === questionKey && e.subsectionKey === item.subsectionKey)
    );

    // Apply same-question clears directly (parity with old behaviour).
    sameQuestionClears.forEach(entry => {
      answerUpdate[entry.varName] = null;
      mergedAllAnswers[entry.varName] = null;
    });

    // If cross-question answers would be cleared, stash the pending save and
    // open the confirmation dialog. The actual save happens in finalizeSave
    // after the user confirms, or is discarded on cancel.
    if (crossQuestionClears.length > 0) {
      pendingSaveRef.current = {
        answerUpdate,
        item,
        crossQuestionClears,
        mergedAllAnswers,
      };
      setClearedAnswersDialogOpen(true);
      return;
    }

    // No cross-question clears — proceed directly to save.
    await finalizeSave(answerUpdate, item, mergedAllAnswers);
  }, [questionSequence, currentQuestionIdx, rawFormInstance, formDefinition, formKey, updateFormAnswers, formState.isEditing, confirmedQuestions, scrollToFormTop, finalizeSave, externalLookups]);

  // ─── handleEdit (called by FormAnswerSummary pencil click) ──────────────────

  const handleEdit = useCallback((submittedIndex) => {
    // Find the question in our sequence that matches the submittedAnswers entry
    const entry = formState.submittedAnswers[submittedIndex];
    if (!entry) return;

    const targetIdx = questionSequence.findIndex(
      item => (item.question.questionVarName || item.questionKey) === entry.questionVarName
    );
    if (targetIdx < 0) return;

    const targetItem = questionSequence[targetIdx];

    // Inline prefill: reset RHF and inject existing answers BEFORE setting isEditing.
    // This mirrors ADHD's handleEdit which does sync prefill, guaranteeing that
    // FormCurrentQuestionPanel renders with existing selections highlighted.
    reset();

    let hasOther = false;
    const question = targetItem.question;
    if (question?.subquestions) {
      Object.entries(question.subquestions).forEach(([sqIdx, sq]) => {
        const varName = sq.serviceUserVarName;
        const rawValue = resolveAnswer(answers, targetItem.questionKey, varName);
        const answerKey = `question_${targetItem.subsectionKey}_${targetItem.questionKey}_${sqIdx}`;
        const normalized = normalizeForRHF(rawValue);

        if (normalized === undefined) return;

        setValue(answerKey, normalized);

        // Set "other" text fields and detect if showOtherInput should be true
        const otherTexts = extractOtherTexts(rawValue);
        Object.entries(otherTexts).forEach(([optionVal, otherText]) => {
          if (sq.otherOptions && sq.otherOptions[optionVal]) {
            const otherKey = otherOptionFieldKey(answerKey, optionVal);
            setValue(otherKey, otherText);
            hasOther = true;
          }
        });

        // Also check if any currently-selected options have otherOptions defined
        if (sq.otherOptions && normalized) {
          const selectedOpts = Array.isArray(normalized) ? normalized : [normalized];
          if (selectedOpts.some(opt => sq.otherOptions[opt])) {
            hasOther = true;
          }
        }
      });
    }

    // Invalidate the nav prefill ref so it doesn't override our inline prefill
    prevPrefillKeyRef.current = `${targetIdx}_${Object.keys(answers || {}).length}`;

    setCurrentQuestionIdx(targetIdx);
    setFormState(prev => ({
      ...prev,
      isEditing: true,
      editIndex: submittedIndex,
      showOtherInput: hasOther,
      currentPanel: Number(targetItem.subsectionKey),
      currentQuestion: Number(targetItem.questionKey),
    }));

    // Explicit scroll — the useEffect on currentQuestionIdx may not fire if the
    // index hasn't changed (e.g. re-editing the same question). Use a short delay
    // so the DOM has re-rendered the question panel before scrolling.
    setTimeout(() => {
      // Land on the subsection title (full question context); fall back to the
      // progress bar then the page top if it isn't present.
      const sectionHeading = document.getElementById('form-section-heading');
      if (sectionHeading) {
        sectionHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const progressBar = document.getElementById('form-progress-bar-container');
      if (progressBar) {
        progressBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  }, [formState.submittedAnswers, questionSequence, answers, reset, setValue]);

  // ─── handleCancelEdit ───────────────────────────────────────────────────────

  const handleCancelEdit = useCallback(() => {
    // Return to the question the user was on before the edit, looked up by
    // identity in the current sequence. No save happened, so questionSequence
    // in the closure is still accurate (unlike handleNextQuestion post-save).
    const returnTo = lastUnansweredQuestion.current;
    let nextIdx = returnTo
      ? questionSequence.findIndex(
          q => q.subsectionKey === returnTo.subsectionKey && q.questionKey === returnTo.questionKey
        )
      : -1;

    // Fallback: if the remembered question is gone (shouldn't happen without a
    // save, but defensive), find the first unanswered question.
    if (nextIdx < 0) {
      nextIdx = questionSequence.findIndex(
        item => !isQuestionAnswered(item.questionKey, item.question, answers)
      );
    }
    if (nextIdx < 0) nextIdx = Math.max(0, questionSequence.length - 1);

    const nextItem = questionSequence[nextIdx];
    setCurrentQuestionIdx(nextIdx);
    setFormState(prev => ({
      ...prev,
      isEditing: false,
      editIndex: null,
      currentPanel: Number(nextItem.subsectionKey),
      currentQuestion: Number(nextItem.questionKey),
    }));
  }, [questionSequence, answers]);

  // ─── Cleared-answers dialog: confirm / cancel ──────────────────────────────
  // When the user confirms, fold the cross-question clears into the pending
  // save payload and run finalizeSave (or finalizeExitSave). When they cancel,
  // discard the pending payload and stay on the current question.

  const handleClearedAnswersConfirm = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending) return;

    // Fold cross-question clears into the save payload.
    const { answerUpdate, crossQuestionClears, mergedAllAnswers, item, partialUpdate } = pending;

    if (pendingExitSave) {
      // Save & Exit path: apply clears to partialUpdate and mergedAll, then save and navigate.
      crossQuestionClears.forEach(entry => {
        if (partialUpdate) partialUpdate[entry.varName] = null;
        mergedAllAnswers[entry.varName] = null;
      });

      const newProgress = calculateProgress(formDefinition, mergedAllAnswers, externalLookups);
      const status = deriveStatusFromProgress(newProgress);
      const extraFields = { progress: newProgress, status };
      const scaleResults = await computeScaleResultsForSave({
        formKey,
        formDefinition,
        answers: mergedAllAnswers,
        progress: newProgress,
      });
      if (scaleResults) {
        extraFields.scaleResults = scaleResults;
      }

      setIsSaving(true);
      try {
        await updateFormAnswers(formKey, partialUpdate || answerUpdate, extraFields);
      } catch (err) {
        console.error('[GenericFormPage] Save on exit failed:', err);
        toast.error("We couldn't save your latest change, but your earlier answers are safe.");
      }
      setIsSaving(false);

      // Clean up dialog state and navigate.
      pendingSaveRef.current = null;
      setClearedAnswersDialogOpen(false);
      setPendingExitSave(false);
      navigate(dashboardPath);
    } else {
      // Normal save path: fold clears and run finalizeSave.
      crossQuestionClears.forEach(entry => {
        answerUpdate[entry.varName] = null;
        mergedAllAnswers[entry.varName] = null;
      });

      pendingSaveRef.current = null;
      setClearedAnswersDialogOpen(false);
      await finalizeSave(answerUpdate, item, mergedAllAnswers);
    }
  }, [pendingExitSave, formDefinition, formKey, updateFormAnswers, navigate, dashboardPath, finalizeSave, externalLookups]);

  const handleClearedAnswersCancel = useCallback(() => {
    // Discard the pending save — the user chose to go back and keep their
    // answers. Stay on the current question in edit mode.
    pendingSaveRef.current = null;
    setClearedAnswersDialogOpen(false);
    setPendingExitSave(false);
  }, []);

  // ─── Navigate back to dashboard (Save & Exit) ──────────────────────────────

  const handleBack = useCallback(async () => {
    // When the form is locked there is nothing to save — go straight to the
    // dashboard. FormNavigation already guards this at the click level, but
    // defence-in-depth: if handleBack is ever called from another path (e.g.
    // browser-back interception) it should not attempt a Firestore write.
    if (isFormLocked) {
      navigate(dashboardPath);
      return;
    }

    // Capture any in-progress typed values from the current question before navigating.
    // This mirrors ADHD PatientFormTemplate.handleSaveAndGoBack which saves partial answers.
    if (currentQuestionIdx !== null && questionSequence[currentQuestionIdx]) {
      const item = questionSequence[currentQuestionIdx];
      const question = item.question;
      if (question?.subquestions) {
        const partialUpdate = {};
        Object.entries(question.subquestions).forEach(([sqIdx, sq]) => {
          const answerKey = `question_${item.subsectionKey}_${item.questionKey}_${sqIdx}`;
          const value = watch(answerKey);
          const varName = sq.serviceUserVarName;
          if (value !== undefined && value !== null && value !== '') {
            // Mirror the main save path exactly: preserve typed details for
            // selectMultiple / selectOne with otherOptions.
            if (sq.inputType === 'selectMultiple' && Array.isArray(value) && value.length > 0) {
              const otherTexts = {};
              value.forEach(opt => {
                const otherKey = otherOptionFieldKey(answerKey, String(opt));
                const otherText = watch(otherKey);
                if (otherText && sq.otherOptions && sq.otherOptions[opt]) {
                  otherTexts[opt] = otherText;
                }
              });
              partialUpdate[varName] = {
                selectedOptions: value,
                ...(Object.keys(otherTexts).length > 0 ? { otherTexts } : {}),
              };
            } else if (sq.inputType === 'selectOne' && sq.otherOptions) {
              const otherKey = otherOptionFieldKey(answerKey, String(value));
              const otherText = watch(otherKey);
              partialUpdate[varName] = (otherText && sq.otherOptions[value])
                ? { selected: value, otherText }
                : value;
            } else if (value !== '') {
              partialUpdate[varName] = value;
            }
          }
        });
        if (Object.keys(partialUpdate).length > 0) {
          const currentAnswers = rawFormInstance?.answers || {};
          const mergedAll = { ...currentAnswers, ...partialUpdate };

          // Check for cross-question clears before saving. Same-question
          // clears apply silently; cross-question clears open the dialog.
          const hiddenEntries = collectNewlyHiddenAnswers(formDefinition, mergedAll, externalLookups);
          const sameQuestionClears = hiddenEntries.filter(
            e => e.questionKey === item.questionKey && e.subsectionKey === item.subsectionKey
          );
          const crossQuestionClears = hiddenEntries.filter(
            e => !(e.questionKey === item.questionKey && e.subsectionKey === item.subsectionKey)
          );

          // Apply same-question clears silently.
          sameQuestionClears.forEach(entry => {
            partialUpdate[entry.varName] = null;
            mergedAll[entry.varName] = null;
          });

          // If cross-question clears would occur, show the dialog and do NOT
          // navigate yet. The confirm handler saves, clears, then navigates.
          if (crossQuestionClears.length > 0) {
            pendingSaveRef.current = {
              answerUpdate: partialUpdate,
              item,
              crossQuestionClears,
              mergedAllAnswers: mergedAll,
              partialUpdate,
            };
            setPendingExitSave(true);
            setClearedAnswersDialogOpen(true);
            return;
          }

          // No cross-question clears — proceed with the normal exit save.
          const newProgress = calculateProgress(formDefinition, mergedAll, externalLookups);
          const status = deriveStatusFromProgress(newProgress);
          const extraFields = { progress: newProgress, status };
          const scaleResults = await computeScaleResultsForSave({
            formKey,
            formDefinition,
            answers: mergedAll,
            progress: newProgress,
          });
          if (scaleResults) {
            extraFields.scaleResults = scaleResults;
          }

          setIsSaving(true);
          try {
            await updateFormAnswers(formKey, partialUpdate, extraFields);
          } catch (err) {
            console.error('[GenericFormPage] Save on exit failed:', err);
            toast.error("We couldn't save your latest change, but your earlier answers are safe.");
          }
          setIsSaving(false);
        }
      }
    }
    navigate(dashboardPath);
  }, [navigate, dashboardPath, currentQuestionIdx, questionSequence, watch, rawFormInstance, formDefinition, formKey, updateFormAnswers, isFormLocked, externalLookups]);

  // ─── Derived State ──────────────────────────────────────────────────────────

  // Always recompute progress from actual answers rather than trusting the stored
  // progress field — the stored value may have been set by a previous implementation
  // using a different format (e.g., count of answered questions instead of 0-1 ratio).
  const computedProgress = useMemo(
    () => calculateProgress(formDefinition, answers, externalLookups),
    [formDefinition, answers, externalLookups]
  );
  const overallProgress = Math.min(100, Math.round(computedProgress * 100));

  // ─── Cross-form progress/status reconciliation ─────────────────────────────
  //
  // Stored `progress` and `status` are normally only written when the parent
  // saves an answer on THIS form. That is enough when every gate lives on the
  // form itself, because nothing can change visibility without a save here.
  // A cross-form gate breaks that assumption: the parent can finish Their
  // early life with only the periods questions left, then say on the About
  // form that the child is Male. The periods questions vanish, live progress
  // is now 100%, but the stored status still says "inProgress", so the
  // dashboard never shows the form as done and "all questionnaires completed"
  // never fires. The reverse flip (Male -> Female) leaves a stored "completed"
  // that hides a newly reopened, unanswered question behind the completion
  // card. This effect brings the stored values back in line with reality.
  //
  // Scope and safety:
  //  - Runs ONLY for forms with an `otherForm` dependency (today: Their early
  //    life). Adult forms and every other CYP form never reach it, so their
  //    legacy progress values are left exactly as they are.
  //  - Never while the form is locked (the server rejects the write anyway).
  //  - Writes no answers and does not touch `confirmedQuestions`; only
  //    `{ progress, status }`, through the same context method every save uses.
  //  - Only writes when the values actually differ, and remembers the last
  //    pair it tried, so a failed write cannot spin into a retry loop.
  //  - The dashboard can still show the stale value until the parent next
  //    opens this form; that is accepted and documented in the plan.
  const lastReconciledRef = useRef(null);
  useEffect(() => {
    if (!hasOtherFormDependency || !rawFormInstance || isFormLocked) return;
    // Nothing to reconcile until the form has been started: a fresh instance
    // has no stored progress and nothing visible has been answered.
    const storedProgress = typeof rawFormInstance.progress === 'number' ? rawFormInstance.progress : 0;
    const storedStatus = rawFormInstance.status || 'notStarted';
    const liveStatus = deriveStatusFromProgress(computedProgress);
    const progressMatches = Math.abs(storedProgress - computedProgress) < 1e-9;
    if (progressMatches && storedStatus === liveStatus) return;

    const attemptKey = `${computedProgress}|${liveStatus}`;
    if (lastReconciledRef.current === attemptKey) return;
    lastReconciledRef.current = attemptKey;

    // Wrapped in Promise.resolve so a synchronous throw or a non-promise
    // return from the context method is handled the same way as a rejection.
    Promise.resolve()
      .then(() => updateFormAnswers(formKey, {}, { progress: computedProgress, status: liveStatus }))
      .catch((err) => {
        // Non-fatal: the next save on this form writes the correct values
        // anyway. Logged so a persistent failure is visible in the console.
        console.warn('[GenericFormPage] Progress reconciliation failed:', err);
      });
  }, [hasOtherFormDependency, rawFormInstance, isFormLocked, computedProgress, formKey, updateFormAnswers]);

  // A form is considered "all answered" if EITHER:
  // 1. Live progress computation reaches 100%, OR
  // 2. The stored status is 'completed' (set by a previous save cycle)
  // This safety net handles edge cases where legacy answer formats don't perfectly
  // map to the new progress computation.
  const allQuestionsAnswered = overallProgress >= 100 || rawFormInstance?.status === 'completed';

  const showConfetti = allQuestionsAnswered && hasSubmittedFinalQuestionOnce && !hasShownConfetti.current;
  if (showConfetti) {
    hasShownConfetti.current = true;
  }

  // Total questions and current question number for the counter display
  const totalQuestions = questionSequence.length;
  const currentQuestionNumber = currentQuestionIdx !== null ? currentQuestionIdx + 1 : null;

  // Build currentPanel and currentQuestion objects matching FormCurrentQuestionPanel's expectations
  const currentItem = currentQuestionIdx !== null ? questionSequence[currentQuestionIdx] : null;

  const currentPanel = useMemo(() => {
    if (!currentItem || !formDefinition?.formSubsections) return null;
    return formDefinition.formSubsections[currentItem.subsectionKey] || null;
  }, [currentItem, formDefinition]);

  const currentQuestion = useMemo(() => {
    if (!currentItem) return null;
    return buildQuestionWithAnswers(currentItem.question, currentItem.questionKey, answers, providerPrefilledKeys, confirmedQuestions);
  }, [currentItem, answers, providerPrefilledKeys, confirmedQuestions]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (!formInstance || currentQuestionIdx === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          {!formInstance && rawFormInstance === undefined ? (
            <>
              <p className="text-gray-500">Form not found. Please return to your dashboard.</p>
              <button
                onClick={() => navigate(dashboardPath)}
                className="mt-4 text-violet-600 hover:text-violet-700 font-medium text-sm"
              >
                Back to Dashboard
              </button>
            </>
          ) : (
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-48 bg-gray-200 rounded mx-auto" />
              <div className="h-3 w-32 bg-gray-100 rounded mx-auto" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    // h-dvh (dynamic viewport height) gives this flex column a definite height
    // so the flex-1 content child is properly constrained and overflow-y-auto
    // creates an inner scroll container. This pins the progress bar above the
    // scroll area — without it, min-h-screen lets the outer div grow beyond
    // the viewport when the answer summary is long, the whole page scrolls,
    // and scrollToFormTop scrolls past the progress bar. h-screen is the
    // fallback for browsers that don't support dvh.
    <div className="h-screen h-dvh bg-gray-50 flex flex-col">
      {/* Gradient banner matching ADHD form visual style */}
      <div className="h-10 md:h-40 bg-gradient-to-r from-pink-200 via-purple-200 to-blue-200 flex-shrink-0" />

      {/* Progress bar + FormNavigation (Save & Exit / Submit and Complete).
          The bottom shadow gives this frozen header a clean visual break
          from the scrollable content below. Without it the header blends
          into the form title and the "Save & Exit" button looks like it
          overlaps the heading text. pb-2 prevents the buttons crowding the
          separator line. */}
      <div className="flex-shrink-0 pb-2 shadow-sm border-b border-gray-200">
        <div className="px-4">
          <FormProgressBar progress={allQuestionsAnswered ? 100 : overallProgress} showConfetti={showConfetti} />
          <FormNavigation
            navigate={navigate}
            shouldShowCompletionCard={allQuestionsAnswered}
            isFormLocked={isFormLocked}
            isEditing={formState.isEditing}
            handleSaveAndGoBack={handleBack}
            dashboardPath={dashboardPath}
          />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        <main className="px-6 md:px-8 py-6">
          {/* Form title — formExplanationText is stored in Firestore for metadata
              but not shown to patients (matches ADHD PatientFormTemplate). */}
          <div className="mb-6 text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{formInstance.formTitle}</h1>
          </div>

          {/* Current question card OR completion state */}
          <div className="border-2 border-violet-300 bg-violet-50 rounded-2xl p-4 md:p-6 shadow-md mb-6 text-left">
            {isFormLocked ? (
              <FormLockedCard
                variant={cypFormsLocked ? 'cypInformant' : undefined}
                childName={cypFormsLocked ? patientFirstName : undefined}
              />
            ) : allQuestionsAnswered && !formState.isEditing ? (
              <FormCompletionCard />
            ) : (
              <FormCurrentQuestionPanel
                handleNextQuestion={handleNextQuestion}
                handleCancelEdit={handleCancelEdit}
                handleSubmit={handleSubmit}
                currentPanel={currentPanel}
                currentQuestion={currentQuestion}
                formState={formState}
                register={register}
                control={control}
                getValues={getValues}
                setValue={setValue}
                setFormState={setFormState}
                totalQuestions={totalQuestions}
                currentQuestionNumber={currentQuestionNumber}
                formKey={formKey}
                userOrganisation={claims?.organisation}
                questionHeadingRef={questionHeadingRef}
                isSaving={isSaving}
                storedAnswerLookup={storedAnswerLookup}
                externalLookups={externalLookups}
              />
            )}
          </div>

          {/* Answer Summary (same component as ADHD) */}
          <FormAnswerSummary
            formState={formState}
            handleEdit={handleEdit}
            isFormLocked={isFormLocked}
            shouldShowCompletionCard={allQuestionsAnswered}
          />
        </main>
      </div>

      {/* Confirmation dialog shown when a save would clear answers from other
          questions that the user cannot see on the current screen. */}
      <ClearedAnswersConfirmDialog
        isOpen={clearedAnswersDialogOpen}
        entries={pendingSaveRef.current?.crossQuestionClears || []}
        onConfirm={handleClearedAnswersConfirm}
        onCancel={handleClearedAnswersCancel}
      />

      {/* CYP safeguarding notice — shown after the parent/carer selects a
          flagged safety option on the riskBehaviours question. Dismissable via
          Escape, overlay click, or the Continue button. The server-side email
          alert fires independently — this is purely presentational. */}
      <CypSafeguardingNoticeDialog
        isOpen={safeguardingNoticeOpen}
        onClose={() => setSafeguardingNoticeOpen(false)}
        organisation={claims?.organisation || 'your healthcare provider'}
        childName={patientFirstName || 'your child'}
      />
    </div>
  );
}
